/* eslint-disable @thebeansgroup/sb/components-file-structure */
import PropTypes from 'prop-types'
import React from 'react'

import { withI18n } from '@thebeansgroup/i18n'

import { ROUTES } from '@src/authorisation/children/router/constants'
import {
  asyncRequestWithFetch,
  getNetworkErrorText,
  responseHasNetworkError,
  trackFormErrorEvent
} from '@src/authorisation/helpers'
import CloudflareTurnstile from '@src/components/cloudflare_turnstile'
import Snowplow from '@src/snowplow'

import Form from '@components/form'

import { EmailField, SubmitButton } from './children/form_components'
import {
  getEmailError,
  isFormValid,
  responseHasEmailNotRegisteredError,
  emailTldIsAcUk,
  getStudentEmailCopy,
  repsonseHasRecaptchaError
} from './helpers'

export class ResetPasswordForm extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      emailError: '',
      loading: false,
      recaptchaError: false,
      recaptchaToken: ''
    }
    this.bindHandlers()
  }

  bindHandlers() {
    this.onEmailValueChange = this.onEmailValueChange.bind(this)
    this.onResetClick = this.onResetClick.bind(this)
    this.handleResetPasswordSuccess = this.handleResetPasswordSuccess.bind(this)
    this.handleResetPasswordFailure = this.handleResetPasswordFailure.bind(this)
    this.handleFormError = this.handleFormError.bind(this)
    this.onEmailFocus = this.onEmailFocus.bind(this)
    this.onEmailBlur = this.onEmailBlur.bind(this)
    this.submitResetPasswordForm = this.submitResetPasswordForm.bind(this)
    this.handleRecaptchaToken = this.handleRecaptchaToken.bind(this)
    this.handleRecaptchaError = this.handleRecaptchaError.bind(this)
    this.handleRecaptchaReset = this.handleRecaptchaReset.bind(this)
    this.handleRecaptchaError = this.handleRecaptchaError.bind(this)
  }

  onEmailValueChange(field, value) {
    this.setState({ emailError: '' })
    this.props.emailValueChangeHandler(value)
  }

  onEmailFocus() {
    this.setState({ emailError: '' })
  }

  onEmailBlur() {
    const { t, email } = this.props
    const error = getEmailError(t, email)
    this.setState({ emailError: error })
  }

  handleNetworkError() {
    const { t } = this.props
    const emailError = getNetworkErrorText(t)

    this.setState({
      loading: false,
      emailError,
      recaptchaError: true
    })

    trackFormErrorEvent('email', emailError)
  }

  handleEmailNotRegistered() {
    const { t, email } = this.props
    const error = t('e_account_not_found')
    this.setState({
      loading: false,
      emailError: error,
      recaptchaError: true
    })
    trackFormErrorEvent('email', error, email)
  }

  handleInvalidDomainTld() {
    const { t, email } = this.props
    const emailError = getStudentEmailCopy(t)

    this.setState({
      loading: false,
      emailError,
      recaptchaError: true
    })

    trackFormErrorEvent('email', emailError, email)
  }

  handleRecaptchaToken(recaptchaToken) {
    this.setState({ recaptchaToken })
  }

  handleRecaptchaError() {
    this.setState({
      loading: false
    })
  }

  handleRecaptchaReset() {
    this.setState({ recaptchaError: false })
  }

  handleFormError(data) {
    this.setState({ loading: false })
    this.props.requestErrorHandler(data)
  }

  handleResetPasswordFailure(data) {
    if (repsonseHasRecaptchaError(data)) {
      return this.handleRecaptchaError()
    }

    if (responseHasEmailNotRegisteredError(data)) {
      if (emailTldIsAcUk(this.props.email)) {
        return this.handleInvalidDomainTld()
      }
      return this.handleEmailNotRegistered()
    } else if (responseHasNetworkError(data)) {
      this.handleNetworkError()
    } else {
      this.handleFormError(data)
    }
  }

  handleResetPasswordSuccess() {
    this.setState({ loading: false })
    Snowplow.trackStructEvent('user', 'submit', null, 'resetpassword')
    this.props.resetPasswordHandler()
  }

  submitResetPasswordForm() {
    const { email, token } = this.props
    const { recaptchaToken } = this.state
    this.setState({ loading: true })

    asyncRequestWithFetch(
      token,
      ROUTES.SERVER.RESET_PASSWORD,
      {
        user: { email },
        'cf-turnstile-response': recaptchaToken
      },
      this.handleResetPasswordSuccess,
      this.handleResetPasswordFailure
    )
  }

  onResetClick() {
    this.runValidations(() => {
      if (isFormValid(this.state)) {
        return this.submitResetPasswordForm()
      }
    })
  }

  runValidations(callback) {
    const { t, email } = this.props
    const error = getEmailError(t, email)
    this.setState(
      {
        emailError: getEmailError(t, email)
      },
      callback
    )
    trackFormErrorEvent('email', error, email)
  }

  renderEmailField() {
    return (
      <EmailField
        t={this.props.t}
        email={this.props.email}
        emailError={this.state.emailError}
        valueHandler={this.onEmailValueChange}
        emailBlurHandler={this.onEmailBlur}
        emailFocusHandler={this.onEmailFocus}
      />
    )
  }

  renderSubmitButton() {
    return (
      <SubmitButton
        t={this.props.t}
        loading={this.state.loading}
        handleFormSubmit={this.onResetClick}
        valid={isFormValid(this.state)}
      />
    )
  }

  renderTurnstile() {
    return (
      <CloudflareTurnstile
        onSuccess={this.handleRecaptchaToken}
        onError={this.handleRecaptchaError}
        error={this.state.recaptchaError}
        resetError={this.handleRecaptchaReset}
        action='password_reset'
      />
    )
  }

  render() {
    return (
      <Form>
        {this.renderEmailField()}
        {this.renderTurnstile()}
        {this.renderSubmitButton()}
      </Form>
    )
  }
}

ResetPasswordForm.propTypes = {
  t: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  requestErrorHandler: PropTypes.func.isRequired,
  resetPasswordHandler: PropTypes.func.isRequired,
  emailValueChangeHandler: PropTypes.func.isRequired,
  country: PropTypes.string
}

export default withI18n(ResetPasswordForm)
